import { useEffect, useMemo, useState } from 'react'
import { api } from '../api'
import {
  Badge,
  Empty,
  Notice,
  Page,
  Panel,
  StatTile,
  Table,
  TileRow,
  fmtDateTime,
  fmtNumber,
} from '../components/ui'
import type { CampaignSummary, PacingDecisionRow, PacingExplain } from '../types'

/**
 * Why the dialler did what it did.
 *
 * Every other dialler on the market answers "why did it slow down at 2pm" with
 * a shrug. This page answers it with the arithmetic: the controller is a pure
 * function, its complete inputs are stored with every decision, and the replay
 * button below feeds those inputs back through the controller running right now
 * to prove the recorded answer is reproducible.
 */

/** Plain-English explanations. A reason code nobody can read is not a reason. */
const GOVERNORS: Record<string, { label: string; why: string; tone?: 'stop' | 'slow' | 'go' }> = {
  model: {
    label: 'Model',
    why: 'Nothing clamped it. The predicted number of agents free within the horizon is what set the line count.',
    tone: 'go',
  },
  cold_start: {
    label: 'Cold start',
    why: 'Too few answered calls to measure an abandonment rate yet, so pacing is held at one line per free agent until there is enough evidence to measure honestly.',
  },
  abandon_pressure: {
    label: 'Easing off',
    why: 'The rolling abandonment rate is climbing toward the campaign ceiling, so the controller is already slowing down — before a breach, not after one.',
    tone: 'slow',
  },
  abandon_ceiling: {
    label: 'Abandonment ceiling',
    why: 'At or over the campaign ceiling. All predictive dialling stopped; only calls matched to an agent who is already idle are permitted, which cannot abandon by construction.',
    tone: 'stop',
  },
  floor_too_small: {
    label: 'Floor too small',
    why: 'Fewer than twelve staffed agents. Predictive dialling is a bet whose error falls as 1/√n, and on a small floor it is not winnable — so the campaign runs one line per idle agent instead.',
    tone: 'stop',
  },
  max_ratio: {
    label: 'Ratio cap',
    why: 'The campaign’s own lines-per-agent cap bound before the model did.',
    tone: 'slow',
  },
  no_capacity: {
    label: 'No capacity',
    why: 'No agent is free or about to be, so there is nothing to dial for.',
  },
  queue_empty: {
    label: 'Queue empty',
    why: 'No dialable leads. Pacing cannot exceed supply.',
  },
  rate_limited: {
    label: 'Rate limited',
    why: 'Demand is being spread across the prediction horizon rather than fired in one burst.',
  },
}

const describe = (g: string) => GOVERNORS[g] ?? { label: g, why: 'Unrecognised governor.' }

export default function Pacing() {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [campaignId, setCampaignId] = useState<string>('')
  const [governor, setGovernor] = useState<string>('')
  const [dialedOnly, setDialedOnly] = useState(false)
  const [rows, setRows] = useState<PacingDecisionRow[]>([])
  const [selected, setSelected] = useState<PacingDecisionRow | null>(null)
  const [replay, setReplay] = useState<PacingExplain | null>(null)
  const [replayErr, setReplayErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    api.campaigns().then(setCampaigns).catch(() => setCampaigns([]))
  }, [])

  useEffect(() => {
    setLoaded(false)
    api
      .pacingDecisions({
        campaign_id: campaignId || undefined,
        governor: governor || undefined,
        // Sent as a string because the query serialiser only carries scalars;
        // omitted entirely when off, so the backend sees no filter at all.
        dialed_only: dialedOnly ? 'true' : undefined,
        limit: 300,
      })
      .then((r) => setRows(r.decisions))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true))
  }, [campaignId, governor, dialedOnly])

  // Replay runs against the controller in the running binary, not against a
  // stored copy of its answer. That is the entire point.
  useEffect(() => {
    setReplay(null)
    setReplayErr(null)
    if (!selected) return
    api
      .pacingExplain(selected.inputs)
      .then(setReplay)
      .catch((e) => setReplayErr(String(e)))
  }, [selected])

  const summary = useMemo(() => {
    const launched = rows.reduce((a, r) => a + r.lines, 0)
    const stops = rows.filter(
      (r) => r.governor === 'abandon_ceiling' || r.governor === 'floor_too_small',
    ).length
    const byGovernor = new Map<string, number>()
    for (const r of rows) byGovernor.set(r.governor, (byGovernor.get(r.governor) ?? 0) + 1)
    return {
      launched,
      stops,
      byGovernor: [...byGovernor.entries()].sort((a, b) => b[1] - a[1]),
    }
  }, [rows])

  const names = useMemo(
    () => new Map(campaigns.map((c) => [c.id, c.name])),
    [campaigns],
  )

  // Does the live controller still agree with what was recorded? Disagreement
  // is not an error — it means the pacing logic changed since — but it is the
  // thing worth surfacing rather than burying.
  const drift =
    selected && replay
      ? replay.lines !== selected.lines || replay.governor !== selected.governor
      : false

  return (
    <Page
      title="Pacing"
      subtitle="Why the dialler launched what it launched"
      actions={
        <div className="range-picker">
          <select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select value={governor} onChange={(e) => setGovernor(e.target.value)}>
            <option value="">Any reason</option>
            {Object.entries(GOVERNORS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <button
            className={dialedOnly ? 'chip on' : 'chip'}
            onClick={() => setDialedOnly((v) => !v)}
          >
            Launches only
          </button>
        </div>
      }
    >
      <TileRow>
        <StatTile label="Decisions" value={fmtNumber(rows.length)} />
        <StatTile label="Lines launched" value={fmtNumber(summary.launched)} tone="live" />
        <StatTile
          label="Compliance stops"
          value={fmtNumber(summary.stops)}
          tone={summary.stops > 0 ? 'warn' : 'ok'}
        />
        <StatTile
          label="Reasons seen"
          value={fmtNumber(summary.byGovernor.length)}
        />
      </TileRow>

      {loaded && rows.length === 0 && (
        <Notice kind="info">
          Nothing recorded yet. The controller only writes a row when it launches a
          call or when its reason changes, so a campaign that has been idle in one
          steady state produces one row rather than one a second.
        </Notice>
      )}

      {summary.byGovernor.length > 0 && (
        <Panel title="What was actually governing">
          <Table head={['Reason', 'Ticks', 'Share', 'What it means']}>
            {summary.byGovernor.map(([g, n]) => {
              const d = describe(g)
              return (
                <tr key={g}>
                  <td>
                    <Badge
                      tone={d.tone === 'stop' ? 'danger' : d.tone === 'go' ? 'ok' : 'warn'}
                    >
                      {d.label}
                    </Badge>
                  </td>
                  <td>{fmtNumber(n)}</td>
                  <td>{((n / rows.length) * 100).toFixed(1)}%</td>
                  <td className="muted">{d.why}</td>
                </tr>
              )
            })}
          </Table>
        </Panel>
      )}

      <Panel title="Decisions">
        {rows.length === 0 ? (
          <Empty message="No pacing decisions recorded." />
        ) : (
          <Table
            head={[
              'When',
              'Campaign',
              'Lines',
              'Reason',
              'Ready',
              'Staffed',
              'In flight',
              'p(human)',
              'Abandon',
              '',
            ]}
          >
            {rows.map((r, i) => (
              <tr
                key={`${r.at}-${i}`}
                className={selected === r ? 'row-selected' : undefined}
              >
                <td className="mono">{fmtDateTime(r.at)}</td>
                <td>{names.get(r.campaign_id) ?? r.campaign_id.slice(0, 8)}</td>
                <td className={r.lines > 0 ? 'strong' : 'muted'}>{r.lines}</td>
                <td>
                  <Badge
                    tone={
                      describe(r.governor).tone === 'stop'
                        ? 'danger'
                        : describe(r.governor).tone === 'go'
                          ? 'ok'
                          : 'warn'
                    }
                  >
                    {describe(r.governor).label}
                  </Badge>
                </td>
                <td>{r.agents_ready}</td>
                <td>{r.agents_staffed}</td>
                <td>{r.calls_in_flight}</td>
                <td className="mono">{(r.p_human * 100).toFixed(1)}%</td>
                <td className="mono">{r.abandon_rate.toFixed(2)}%</td>
                <td>
                  <button className="chip" onClick={() => setSelected(r)}>
                    Explain
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {selected && (
        <Panel
          title={`Decision at ${fmtDateTime(selected.at)}`}
          actions={
            <button className="chip" onClick={() => setSelected(null)}>
              Close
            </button>
          }
        >
          <p className="muted">{describe(selected.governor).why}</p>

          {replayErr && <Notice kind="error">Could not replay: {replayErr}</Notice>}

          {replay && (
            <Notice kind={drift ? 'warn' : 'success'}>
              {drift ? (
                <>
                  <strong>The controller no longer agrees.</strong> Replaying the
                  stored inputs through the running build gives{' '}
                  <strong>{replay.lines}</strong> lines under{' '}
                  <strong>{describe(replay.governor).label}</strong>, where{' '}
                  <strong>{selected.lines}</strong> under{' '}
                  <strong>{describe(selected.governor).label}</strong> was recorded.
                  The pacing logic changed between then and now.
                </>
              ) : (
                <>
                  <strong>Reproduced.</strong> Replaying the stored inputs through
                  the controller running right now returns the same{' '}
                  <strong>{replay.lines}</strong> lines under the same reason. The
                  recorded decision is not a log line — it is a computation that
                  can be re-run.
                </>
              )}
            </Notice>
          )}

          <div className="explain-grid">
            <Fact
              k="Agents free now"
              v={selected.inputs.agents_ready}
              note="Idle this instant, with a live leg."
            />
            <Fact
              k="Freeing within horizon"
              v={selected.inputs.agents_freeing_soon}
              note="Counted from each agent's own timer, never from a pool average."
            />
            <Fact k="Talking" v={selected.inputs.agents_talking} />
            <Fact k="Wrapping up" v={selected.inputs.agents_wrapping} />
            <Fact
              k="Reserved"
              v={selected.inputs.agents_reserved}
              note="Already promised to a dial in flight, so not capacity."
            />
            <Fact
              k="Calls ringing"
              v={selected.inputs.calls_in_flight}
              note="Supply that has not landed yet."
            />
            <Fact
              k="Answer rate"
              v={`${(selected.inputs.answer_rate * 100).toFixed(1)}%`}
              note="Smoothed toward a pessimistic prior, so a lucky first minute cannot convince the controller a list is better than it is."
            />
            <Fact
              k="Machine rate"
              v={`${(selected.inputs.machine_rate * 100).toFixed(1)}%`}
            />
            <Fact
              k="p(human)"
              v={`${(selected.p_human * 100).toFixed(1)}%`}
              note="Answer rate less the share that turn out to be machines. Dial 1/p to produce one conversation."
            />
            <Fact
              k="Horizon"
              v={`${selected.inputs.avg_ring_sec.toFixed(1)}s`}
              note="Mean time to answer. A call launched now needs an agent free that far ahead, not one free this instant."
            />
            <Fact k="Mean talk" v={`${selected.inputs.avg_talk_sec.toFixed(0)}s`} />
            <Fact k="Mean wrap" v={`${selected.inputs.avg_wrap_sec.toFixed(0)}s`} />
            <Fact
              k="Abandonment"
              v={`${selected.inputs.abandon_rate.toFixed(2)}% of ${fmtNumber(selected.inputs.answered_sample)}`}
              note="Rolling 30 days. Below 100 answered calls the rate is noise and the ceiling is not applied — pacing is held timid instead."
            />
            <Fact
              k="Campaign ceiling"
              v={`${selected.inputs.abandon_target.toFixed(2)}%`}
            />
            <Fact k="Ratio cap" v={`${selected.inputs.max_ratio.toFixed(2)}×`} />
            <Fact k="Leads available" v={fmtNumber(selected.inputs.queue_depth)} />
            <Fact
              k="Safety margin"
              v={`${(selected.inputs.safety_margin * 100).toFixed(0)}%`}
              note="Aim below predicted capacity rather than at it, because answers arrive in clusters."
            />
            <Fact
              k="Carried fraction"
              v={selected.inputs.carry.toFixed(3)}
              note="A small floor wants less than one dial per second; the remainder is carried rather than floored away."
            />
          </div>

          {replay && (
            <div className="explain-grid">
              <Fact
                k="Predicted capacity"
                v={replay.predicted_capacity.toFixed(2)}
                note="Agents expected free within the horizon, after the safety margin."
              />
              <Fact
                k="Expected from ringing calls"
                v={replay.expected_from_inflight.toFixed(2)}
              />
              <Fact k="Deficit" v={replay.deficit.toFixed(3)} />
              <Fact
                k="Aggressiveness"
                v={replay.aggressiveness.toFixed(2)}
                note="Falls to zero as the rolling rate reaches the ceiling, so the controller eases off before it breaches."
              />
            </div>
          )}
        </Panel>
      )}
    </Page>
  )
}

function Fact({ k, v, note }: { k: string; v: string | number; note?: string }) {
  return (
    <div className="fact">
      <div className="fact-k">{k}</div>
      <div className="fact-v">{v}</div>
      {note && <div className="fact-note">{note}</div>}
    </div>
  )
}
